import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { BaseService } from "src/app/@core/services/base.service";
import { environment } from "src/environments/environment.dev";
import { ITemplateLibrary } from "../components/settings/library/template-library/template-library.component";
import { IAttachment, IDocumentVersion } from "src/app/@core/models/model";

export enum TEMPLATE_GENERATION_STATUS {
    COMPLETED = 'COMPLETED',
    IN_PROGRESS = 'IN_PROGRESS',
}

@Injectable({ providedIn: 'root' })
export class TemplateLibraryService extends BaseService {

    constructor(
        protected override http: HttpClient
    ) {
        super(http);
    }

    apiUrl = environment.apiUrl + '/template-library';

    createTemplate(formData: FormData) {
        return this.post<ITemplateLibrary>(this.apiUrl + '/create', formData);
    }

    getTemplates(filters: {
        skip?: number;
        limit?: number;
        search?: string;
        createdDate?: { from?: string; to?: string };
        lastModifiedDate?: { from?: string; to?: string };
        author?: string;
        documentType?: string;
    }) {
        return this.post<{
            data: ITemplateLibrary[],
            total: number,
        }>(this.apiUrl + '/', filters);
    }

    getTemplate(templateId: string) {
        return this.get<(ITemplateLibrary & { generationStatus: TEMPLATE_GENERATION_STATUS })>(this.apiUrl + '/' + templateId);
    }

    // Version methods
    getTemplateVersions(templateId: string, filters?: { search: string, skip: number, limit: number }) {
        return this.post<{
            versions: IDocumentVersion[],
            total: number
        }>(this.apiUrl + '/version/' + templateId, { filters });
    }

    getTemplateVersionURL(templateId: string, versionId: string) {
        return this.get<{ url: string, key: string, attachment: IAttachment }>(this.apiUrl + '/version/template-url/' + templateId + '/' + versionId);
    }

    restoreTemplateVersion(templateId: string, versionId: string) {
        return this.post(this.apiUrl + '/version/restore/' + templateId + '/' + versionId, {});
    }

    downloadTemplateVersion(templateId: string, docVersionId: string) {
        return this.get<{ data: Uint8Array }>(this.apiUrl + '/version/download/' + docVersionId + '/' + templateId);
    }

    saveTemplateVersion(templateId: string) {
        return this.post(this.apiUrl + '/version', { template: templateId });
    }

    deleteTemplateVersion(templateId: string, versionId: string) {
        return this.delete(this.apiUrl + '/version/' + templateId + '/' + versionId);
    }

    updateTemplateVersionName(templateId: string, versionId: string, name: string) {
        return this.put(this.apiUrl + '/version/' + templateId + '/' + versionId, { name });
    }

    // Audit logs methods
    getTemplateAuditLogs(templateId: string) {
        return this.get<any[]>(this.apiUrl + '/audit-logs/' + templateId);
    }

    // Template metadata update
    updateTemplateMetadata(templateId: string, data: { name?: string; description?: string; templateId?: string }) {
        return this.put<ITemplateLibrary>(this.apiUrl + '/edit/' + templateId, data);
    }

    // Delete template
    deleteTemplate(templateId: string) {
        return this.delete(this.apiUrl + '/' + templateId);
    }

    // Bulk delete templates
    bulkDeleteTemplates(templateIds: string[]) {
        return this.delete<{ deletedCount: number }>(this.apiUrl + '/', { templateIds });
    }

    // Clone template
    cloneTemplate(templateId: string) {
        return this.post<ITemplateLibrary>(this.apiUrl + '/clone/' + templateId, {});
    }

    // Download template
    downloadTemplate(templateId: string) {
        return this.get<{ data: Uint8Array }>(this.apiUrl + '/download/' + templateId);
    }

    // Bulk download templates
    bulkDownloadTemplates(templateIds: string[]) {
        return this.post<Array<{ templateId: string; filename: string; buffer: string; mimeType: string }>>(this.apiUrl + '/download', { templateIds });
    }

    // Force save template
    forceSaveTemplate(templateId: string, key: string, userId?: string) {
        return this.post(this.apiUrl + '/' + templateId + '/force-save', { key, userId });
    }
}